import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Home, Shield, Clock } from 'lucide-react';
import ItLoganLogo from './img/itlogan.svg';

function NotFound() {
  const [timeLeft, setTimeLeft] = useState(10);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    // Log the unauthorized access attempt
    const logUnauthorizedAccess = async () => {
      try {
        await fetch('/api/log-404', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            path: window.location.pathname,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            ip: 'logged by server'
          })
        });
      } catch (error) {
        // Silent fail - don't expose logging to potential intruders
      }
    };

    logUnauthorizedAccess();

    // Start countdown timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsRedirecting(true);
          setTimeout(() => {
            window.location.href = '/';
          }, 1000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center glassmorphism-bg relative overflow-hidden">
      {/* Background animated elements */}
      <div className="glassmorphism-orb glassmorphism-orb-1"></div>
      <div className="glassmorphism-orb glassmorphism-orb-2"></div>
      <div className="glassmorphism-orb glassmorphism-orb-3"></div>
      
      <div className="glassmorphism-card w-full max-w-lg relative z-10 text-center">
        <img 
          src={ItLoganLogo} 
          alt="IT Logan Logo" 
          className="mx-auto mb-6" 
          style={{ width: '200px', height: 'auto', objectFit: 'contain' }} 
        />
        
        <div className="mb-6">
          <AlertTriangle className="h-20 w-20 text-red-400 mx-auto mb-4 animate-pulse" />
          <h1 className="text-4xl font-bold text-white mb-2">Access Denied</h1>
          <h2 className="text-xl text-red-400 mb-4">404 - Page Not Found</h2>
        </div>

        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center mb-3">
            <Shield className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-400 font-semibold">Security Notice</span>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            The page you are looking for has been removed, is temporarily unavailable, 
            or you do not have the required permissions to access this resource.
          </p>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center mb-3">
            <Clock className="h-5 w-5 text-yellow-400 mr-2" />
            <span className="text-yellow-400 font-semibold">Auto Redirect</span>
          </div>
          <p className="text-gray-300 text-sm">
            {isRedirecting ? (
              "Redirecting to home page..."
            ) : (
              <>You will be redirected to the home page in <span className="text-yellow-400 font-bold">{timeLeft}</span> seconds</>
            )}
          </p>
        </div>

        <div className="space-y-3">
          <Link 
            to="/"
            className="glassmorphism-button w-full py-3 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95 relative overflow-hidden group focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 flex items-center justify-center"
          >
            <Home className="h-5 w-5 mr-2" />
            Return to Home Page
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
          </Link>
        </div>

        <div className="mt-8 text-xs text-gray-400 text-center">
          <p className="mb-2">All access attempts are monitored and logged</p>
          <p className="opacity-80">
            If you believe you should have access to this page, please contact the system administrator.
          </p>
          <div className="mt-4 font-medium text-gray-300">
            Ramon Logan Jr. © 2025 IT Logan. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotFound;
